/**
 * Capture-evidence artefacts (S7-6 — droom self-capture).
 *
 * The customer's document photo + selfie frame, uploaded to the session's
 * server-side evidence store. Wire contract (LOCKED — vCIP_be is built against
 * this verbatim):
 *
 *   GET  /api/v1/sessions/{id}/artifacts     → { artifacts: ArtifactMeta[] }
 *        Metadata ONLY — the droom can never re-download evidence bytes.
 *   POST /api/v1/sessions/{id}/artifacts     → multipart: kind + file (jpeg)
 *        201 one ArtifactMeta · 409 kind already captured (WRITE-ONCE — treat
 *        as success and refresh the list; a second image never replaces the
 *        first) · 422 message names the problem (CONSENT_REQUIRED / capture
 *        window closed / bad file) · 404 unknown session.
 *   POST /api/v1/sessions/{id}/queue:enter   → requires both kinds + consent
 *        server-side; 200 echoes the actual FSM state (idempotent) · 422
 *        message names exactly what's missing.
 *
 * Evidence honesty: the sha256 in the metadata is the SERVER's hash of what it
 * stored — the UI shows it as an integrity cue, never as a "verified" claim
 * (verification = server-side pre-join checks + the live officer).
 *
 * Mock mode (no backend) resolves locally with fixture METADATA about the
 * local blob — it never pretends a server bound the evidence (consent.ts mock
 * convention: no evidence rail exists, so nothing claims one).
 */

import { delay, isMockMode, request } from './client';

/**
 * SELFIE_WITH_DOC_FRAME (INTAKE-09 §3.1): the customer + document co-framed in ONE
 * frame. Capture-only by registry ruling — `origin=LIVE_CAPTURE` only, "no upload
 * door, ever"; the droom never offers a file picker for it and the server enforces
 * the same. A be without the kind yet rejects it (deploy skew — the step fails soft).
 */
export type ArtifactKind = 'DOC_PHOTO' | 'SELFIE_FRAME' | 'SELFIE_WITH_DOC_FRAME';

/** Artefact metadata echoed by the backend (never the bytes). */
export interface ArtifactMeta {
  kind: ArtifactKind;
  contentType: string;
  sha256: string;
  capturedAt: string;
}

/** queue:enter echo — `status` is the backend FSM's actual state, not a claim. */
export interface QueueEntryResult {
  sessionId: string;
  status: string;
  note: string;
}

/**
 * Marker the backend puts in a 422 message when the session has no consent
 * record — the UI must route the customer back to the consent step (capture
 * of biometric/document evidence must FOLLOW recorded consent, DPDP ordering).
 */
export const CONSENT_REQUIRED_MARKER = 'CONSENT_REQUIRED';

/** List the artefact kinds already bound to this session (resume support). */
export async function listArtifacts(sessionId: string): Promise<ArtifactMeta[]> {
  if (isMockMode()) {
    // Mock: no server evidence store exists — nothing is ever "already captured".
    await delay(220);
    return [];
  }
  const res = await request<{ artifacts: ArtifactMeta[] | null }>(
    `/api/v1/sessions/${encodeURIComponent(sessionId)}/artifacts`,
  );
  return res.artifacts ?? [];
}

/** Upload one captured frame. 409 = that kind is already bound (write-once). */
export async function uploadArtifact(
  sessionId: string,
  kind: ArtifactKind,
  blob: Blob,
): Promise<ArtifactMeta> {
  if (isMockMode()) {
    // Fixture metadata about the LOCAL blob (a real local hash when WebCrypto
    // allows) — honest about being unbound: no server received anything.
    await delay(600);
    return {
      kind,
      contentType: blob.type || 'image/jpeg',
      sha256: (await localSha256Hex(blob)) ?? 'mock-sha256-unavailable',
      capturedAt: new Date().toISOString(),
    };
  }
  const form = new FormData();
  form.append('kind', kind);
  form.append('file', blob, `${kind.toLowerCase()}.jpg`);
  return request<ArtifactMeta>(
    `/api/v1/sessions/${encodeURIComponent(sessionId)}/artifacts`,
    { method: 'POST', body: form },
  );
}

/**
 * Enter the verifier queue. Server-side gate: both artefact kinds + a consent
 * record must exist. Idempotent — a 200 echoes wherever the FSM actually is.
 */
export async function enterQueue(sessionId: string): Promise<QueueEntryResult> {
  if (isMockMode()) {
    await delay(320);
    return { sessionId, status: 'IN_QUEUE', note: 'mock — no backend queue exists' };
  }
  return request<QueueEntryResult>(
    `/api/v1/sessions/${encodeURIComponent(sessionId)}/queue:enter`,
    { method: 'POST' },
  );
}

/** Local SHA-256 (hex) of a blob — mock-mode integrity cue only. */
async function localSha256Hex(blob: Blob): Promise<string | null> {
  try {
    if (typeof crypto === 'undefined' || !crypto.subtle) return null;
    const digest = await crypto.subtle.digest('SHA-256', await blob.arrayBuffer());
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  } catch {
    return null;
  }
}
